
import React, { useState } from 'react';
import { Company } from '@/lib/types';
import CompanyHeader from './CompanyHeader';
import CompanyRiskAssessment from './CompanyRiskAssessment';
import RecentEmployeeExits from './RecentEmployeeExits';
import AIAdvisor from './AIAdvisor';
import CategoryFilter from './CategoryFilter';
import DataSources from './DataSources';
import CompanyReviews from './CompanyReviews';
import JobAdvertisements from './JobAdvertisements';
import ShareRedFlagLink from './ShareRedFlagLink';
import ClaimCompanyBox from './ClaimCompanyBox';
import CompanyChatWidget from '@/components/company-chat/CompanyChatWidget';
import { useMobileDetection } from '@/hooks/useMobileDetection';

interface ProfileContentProps {
  company: Company | null;
  decodedCompanyName: string;
  companyStats: {
    totalReviews: number;
    locations: string[];
  };
  reviewData: {
    filteredReviews: any[];
    positiveExperiences: number;
    negativeExperiences: number;
    fairWorkCount: number;
    currentPage: number;
    totalPages: number;
    currentReviews: any[];
    handlePageChange: (pageNumber: number) => void;
  };
  categoryData: {
    selectedCategory: string;
    setSelectedCategory: (category: string) => void;
    JOB_CATEGORIES: string[];
  };
  isLoading: boolean;
}

const ProfileContent: React.FC<ProfileContentProps> = ({ 
  company, 
  decodedCompanyName,
  companyStats,
  reviewData,
  categoryData,
  isLoading
}) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const mobileInfo = useMobileDetection();

  // Log component load with device info
  React.useEffect(() => {
    console.log('ProfileContent loaded for company:', decodedCompanyName, {
      mobileInfo,
      isLoading,
      companyExists: !!company
    });
  }, [decodedCompanyName, mobileInfo, isLoading, company]);

  const handleStartChat = () => {
    setIsChatOpen(true);
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
  };

  return (
    <div className="container px-4 mx-auto">
      <div className="mb-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Left side: Company Header */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-redflags-light/20">
              <CompanyHeader 
                company={company}
                companyName={decodedCompanyName}
                locations={companyStats.locations}
                positiveExperiences={reviewData.positiveExperiences}
                negativeExperiences={reviewData.negativeExperiences}
                fairWorkCount={reviewData.fairWorkCount}
                isLoading={isLoading}
              />
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-6 pb-8 mb-6 border border-redflags-light/20">
              <CategoryFilter 
                categories={categoryData.JOB_CATEGORIES}
                selectedCategory={categoryData.selectedCategory}
                onCategoryChange={categoryData.setSelectedCategory}
              />
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-redflags-light/20">
              <DataSources companyName={decodedCompanyName} />
            </div>
          </div>
          
          {/* Right side: Risk Assessment, Recent Employee Exits, and AI Advisor */}
          <div className="md:col-span-1">
            <CompanyRiskAssessment 
              companyName={decodedCompanyName}
              reviewCount={reviewData.filteredReviews.length}
              reviews={reviewData.filteredReviews}
            />
            
            <div className="mb-6">
              <RecentEmployeeExits reviews={reviewData.filteredReviews} />
            </div>
            
            <div className="mb-6">
              <AIAdvisor 
                companyName={decodedCompanyName}
                onStartChat={handleStartChat}
              />
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-redflags-light/20">
        <CompanyReviews
          companyReviews={reviewData.filteredReviews}
          companyName={decodedCompanyName}
          currentPage={reviewData.currentPage}
          totalPages={reviewData.totalPages}
          currentReviews={reviewData.currentReviews}
          handlePageChange={reviewData.handlePageChange}
          isLoading={isLoading}
        />
      </div>
      
      <div className="mt-16">
        <JobAdvertisements companyName={decodedCompanyName} />
        <ShareRedFlagLink companyName={decodedCompanyName} />
        
        <ClaimCompanyBox 
          companyName={decodedCompanyName}
          companyId={company?.id}
        />
      </div>
      
      {/* Company Chat Widget - only show when chat is opened */}
      {isChatOpen && (
        <CompanyChatWidget 
          companyName={decodedCompanyName} 
          onClose={handleCloseChat}
        />
      )}
    </div>
  );
};

export default ProfileContent;
